import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { getPropertyRatings } from '../../api/services/hostService';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Star, Wifi, Utensils, Sparkles, AlertCircle, Loader2, InboxIcon, MessageSquareQuote } from 'lucide-react';

interface RatingsData {
  averages: {
    cleaning: number;
    internet: number;
    food: number;
  };
  reviews: {
    feedback_text: string;
    rating__tenant__name: string;
  }[];
}

interface RatingsAndReviewsProps {
  propertyId: string;
}

// ── Star bar visual ──────────────────────────────────────────────────────────
const StarBar: React.FC<{ value: number }> = ({ value }) => {
  const pct = Math.min(Math.max((value / 5) * 100, 0), 100);
  return (
    <div className="flex items-center gap-2 mt-1">
      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full bg-yellow-400 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground w-6 text-right">{value.toFixed(1)}</span>
    </div>
  );
};

// ── Individual rating card ────────────────────────────────────────────────────
const RatingCard: React.FC<{
  label: string;
  value: number;
  icon: React.ReactNode;
  accent: string;
  accentBg: string;
  accentBorder: string;
}> = ({ label, value, icon, accent, accentBg, accentBorder }) => (
  <div
    className="rounded-xl border p-4 flex flex-col gap-2"
    style={{ background: accentBg, borderColor: accentBorder }}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2" style={{ color: accent }}>
        {icon}
        <span className="text-sm font-semibold">{label}</span>
      </div>
      <div className="flex items-center gap-1">
        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
        <span className="text-xl font-bold" style={{ color: accent }}>
          {value.toFixed(1)}
        </span>
        <span className="text-xs text-muted-foreground">/5</span>
      </div>
    </div>
    <StarBar value={value} />
  </div>
);

const RatingsAndReviews: React.FC<RatingsAndReviewsProps> = ({ propertyId }) => {
  const [ratingsData, setRatingsData] = useState<RatingsData | null>(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format('YYYY-MM'));

  const monthOptions = Array.from({ length: 13 }, (_, i) => dayjs().subtract(i, 'month').format('YYYY-MM'));

  useEffect(() => {
    if (!propertyId) return;
    const fetchRatings = async () => {
      setLoading(true);
      setError(null);
      try {
        const [y, m] = selectedMonth.split('-').map(Number);
        const data = await getPropertyRatings(propertyId, y, m);
        setRatingsData(data);
      } catch (err) {
        setError('Failed to fetch ratings data.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchRatings();
  }, [propertyId, selectedMonth]);

  const overallAvg =
    ratingsData
      ? ((ratingsData.averages.cleaning +
          ratingsData.averages.internet +
          ratingsData.averages.food) /
          3)
      : null;

  return (
    <div className="space-y-5">

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Star className="h-5 w-5 text-yellow-500 fill-yellow-400" />
          <h2 className="text-lg font-semibold text-foreground">Ratings & Reviews</h2>
        </div>

        {/* Filter */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Month</label>
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-[120px] bg-background">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent className="bg-card border z-50">
              {monthOptions.map(month => (
                <SelectItem key={month} value={month}>{month}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ── Loading ─────────────────────────────────────────────────────────── */}
      {loading && (
        <div className="flex justify-center items-center py-16">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
        </div>
      )}

      {/* ── Error ───────────────────────────────────────────────────────────── */}
      {!loading && error && (
        <div className="flex items-center gap-2 rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {/* ── Content ─────────────────────────────────────────────────────────── */}
      {!loading && !error && ratingsData && (
        <div className="space-y-6">

          {/* Overall score pill */}
          {overallAvg !== null && (
            <div className="flex items-center gap-3 rounded-xl border border-yellow-200 bg-yellow-50 px-4 py-3">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star
                    key={s}
                    className={`h-4 w-4 ${
                      s <= Math.round(overallAvg)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-yellow-200'
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm font-semibold text-yellow-800">
                Overall {overallAvg.toFixed(1)} / 5
              </span>
              <span className="text-xs text-yellow-600 ml-auto">
                {dayjs(selectedMonth).format('MMMM YYYY')}
              </span>
            </div>
          )}

          {/* ── Average rating cards ────────────────────────────────────────── */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              Category Averages
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <RatingCard
                label="Cleaning"
                value={ratingsData.averages.cleaning}
                icon={<Sparkles className="h-4 w-4" />}
                accent="#1B3A32"
                accentBg="#f0faf5"
                accentBorder="#bbddd0"
              />
              <RatingCard
                label="Internet"
                value={ratingsData.averages.internet}
                icon={<Wifi className="h-4 w-4" />}
                accent="#1e40af"
                accentBg="#eff6ff"
                accentBorder="#bfdbfe"
              />
              <RatingCard
                label="Food"
                value={ratingsData.averages.food}
                icon={<Utensils className="h-4 w-4" />}
                accent="#92400e"
                accentBg="#fffbeb"
                accentBorder="#fde68a"
              />
            </div>
          </div>

          {/* ── Reviews ─────────────────────────────────────────────────────── */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              Monthly Reviews
            </h3>

            {ratingsData.reviews.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-14 text-muted-foreground gap-3">
                <InboxIcon className="h-10 w-10 opacity-30" />
                <p className="text-sm font-medium">No reviews this month</p>
                <p className="text-xs opacity-70">
                  Share the rating link above so tenants can submit feedback.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {ratingsData.reviews.map((review, index) => (
                  <div
                    key={index}
                    className="rounded-xl border border-border bg-card p-4 space-y-2"
                  >
                    <div className="flex items-start gap-2">
                      <MessageSquareQuote className="h-4 w-4 text-yellow-500 shrink-0 mt-0.5" />
                      <p className="text-sm text-foreground leading-relaxed italic">
                        "{review.feedback_text}"
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground text-right font-medium">
                      — {review.rating__tenant__name}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RatingsAndReviews;
